import { isValidAbsoluteUrl, normalizeUrl, safeConcatPath } from '@/helpers/url'
import { getInviteServiceDetails } from '@/resources/service'

export const makeServiceUrl = (url, serviceId) => {
  const { urlRoot } = getInviteServiceDetails(serviceId)
  const isSearch = urlRoot.endsWith('=')
  if (isSearch) {
    return `${urlRoot}${url}`
  }
  return safeConcatPath(urlRoot, url)
}

export const getInviteLink = (url, serviceId) => {
  // Always return absolute URLs, regardless of the service
  if (isValidAbsoluteUrl(url)) {
    return url
  }

  if (serviceId === 'website') {
    // If it's a website and it wasn't a valid absolute URL, do our best to normalize it and convert it to a valid URL.
    return normalizeUrl(url)
  }

  return makeServiceUrl(url, serviceId)
}

export const isDiscordService = (serviceId) => {
  return (
    serviceId === 'discord' ||
    serviceId === 'discord-server' ||
    serviceId === 'discord-user'
  )
}

export function sessionAppliesToSelectedSplits(session, selectedSplitIds = []) {
  if (selectedSplitIds.length === 0) {
    return false
  }

  const sessionSplits = Array.isArray(session?.splits) ? session.splits : []

  if (sessionSplits.length === 0) {
    return true
  }

  return sessionSplits.some((split) => {
    const splitId = split?.championshipSplitId || split?.championshipSplit?.id
    return splitId ? selectedSplitIds.includes(splitId) : true
  })
}

export function eventAppliesToSelectedSplits(event, selectedSplitIds = []) {
  const sessions = Array.isArray(event?.sessions) ? event.sessions : []

  if (sessions.length === 0) {
    return false
  }

  return sessions.some((session) =>
    sessionAppliesToSelectedSplits(session, selectedSplitIds),
  )
}

export function getSplitId(split) {
  return split?.championshipSplitId || split?.championshipSplit?.id || null
}

export function splitHasDisplayOverrides(split) {
  return Boolean(split?.targetDateTime || split?.duration || split?.durationType)
}

export function applySplitDisplayValues(session, split) {
  const splitId = getSplitId(split)

  return {
    ...session,
    id: `${session.id}-${splitId || 'shared'}`,
    targetDateTime: split?.targetDateTime || session.targetDateTime,
    duration: split?.duration || session.duration,
    durationType: split?.durationType || session.durationType,
    splits: split ? [split] : [],
  }
}

export function getDisplaySessionsForSelectedSplits(
  session,
  selectedSplitIds,
) {
  if (!sessionAppliesToSelectedSplits(session, selectedSplitIds)) {
    return []
  }

  const sessionSplits = Array.isArray(session?.splits) ? session.splits : []

  if (sessionSplits.length === 0) {
    return [session]
  }

  const selectedSessionSplits = sessionSplits.filter((split) => {
    const splitId = getSplitId(split)
    return splitId && selectedSplitIds.includes(splitId)
  })

  if (selectedSessionSplits.length === 0) {
    return []
  }

  if (selectedSessionSplits.some(splitHasDisplayOverrides)) {
    return selectedSessionSplits.map((split) =>
      applySplitDisplayValues(session, split),
    )
  }

  return [
    {
      ...session,
      splits: selectedSessionSplits,
    },
  ]
}

function getDurationMs(duration, durationType) {
  const numericDuration = Number(duration)

  if (!Number.isFinite(numericDuration)) {
    return 0
  }

  switch (durationType) {
    case 'min':
      return numericDuration * 60 * 1000
    case 'hour':
      return numericDuration * 60 * 60 * 1000
    case 'day':
      return numericDuration * 24 * 60 * 60 * 1000
    case 'lap':
      return numericDuration * 90 * 1000
    case 'percent':
      return numericDuration * 72 * 1000
    default:
      return 0
  }
}

function getSessionFinishDateTime(session) {
  if (session?.finishDateTime && !splitHasDisplayOverrides(session)) {
    return session.finishDateTime
  }

  if (!session?.targetDateTime) {
    return null
  }

  return new Date(
    new Date(session.targetDateTime).getTime() +
      getDurationMs(session.duration, session.durationType),
  ).toISOString()
}

export function getEventScheduleForSelectedSplits(event, selectedSplitIds) {
  const sessions = Array.isArray(event?.sessions) ? event.sessions : []
  const displaySessions = sessions.flatMap((session) =>
    getDisplaySessionsForSelectedSplits(session, selectedSplitIds),
  )

  const sessionStartTimes = displaySessions
    .map((session) => session.targetDateTime)
    .filter(Boolean)
    .map((targetDateTime) => new Date(targetDateTime).getTime())
    .filter(Number.isFinite)
  const sessionFinishTimes = displaySessions
    .map((session) => getSessionFinishDateTime(session))
    .filter(Boolean)
    .map((finishDateTime) => new Date(finishDateTime).getTime())
    .filter(Number.isFinite)

  if (sessionStartTimes.length === 0) {
    return event
  }

  const startDateTime = new Date(Math.min(...sessionStartTimes)).toISOString()
  const finishDateTime =
    sessionFinishTimes.length > 0
      ? new Date(Math.max(...sessionFinishTimes)).toISOString()
      : event.finishDateTime

  return {
    ...event,
    startDateTime,
    finishDateTime,
  }
}
